import {
  AbilityBuilder,
  AbilityTuple,
  createMongoAbility,
  MongoAbility,
  MongoQuery,
} from "@casl/ability";
import { StaffUserRole, StripeAccountType } from "@gymflow/types";

import { Subject } from "./Subject";
import { Verb } from "./Verb";

const setupPermissions = ({
  builder,
  role,
  userId,
  stripeAccountType,
}: {
  builder: AbilityBuilder<MongoAbility<AbilityTuple, MongoQuery>>;
  role: StaffUserRole;
  userId: string;
  stripeAccountType: StripeAccountType;
}) => {
  const { can, cannot } = builder;
  if (role === "OWNER") {
    can(Verb.View, Subject.Dashboard);
    can(Verb.View, Subject.StaffCalendar);
    can(Verb.View, Subject.Leads);
    can(Verb.View, Subject.MemberList);
    can(Verb.Download, Subject.MemberList);
    can(Verb.View, Subject.MemberProfile);
    can(Verb.Create, Subject.Email);
    can(Verb.Create, Subject.SMS);
    can(Verb.View, Subject.Settings);
    can(Verb.View, Subject.Sales);
    can(Verb.View, Subject.Reports);
    can(Verb.View, Subject.GlobalSearch);
    can(Verb.Credit, Subject.Invoices);
    can(Verb.Collect, Subject.Invoices);
    can(Verb.Refund, Subject.Invoices);
    can(Verb.WriteOff, Subject.Invoices);
    can(Verb.View, Subject.Marketing);
    can(Verb.View, Subject.Task);
    can(Verb.View, Subject.StaffAccount);
    can(Verb.View, Subject.Appointments);
    can(Verb.View, Subject.AppointmentAvailability);
    can(Verb.View, Subject.FacilityAvailability);
    can(Verb.View, Subject.Access);
    can(Verb.Edit, Subject.CheckoutPrice);
    can(Verb.Create, Subject.XeroConnect);
    can(Verb.Edit, Subject.XeroConnect);
    can(Verb.Create, Subject.ZapierConnect);
    can(Verb.Edit, Subject.ZapierConnect);
    can(Verb.View, Subject.Integrations);
    can(Verb.View, Subject.Payments);
    can(Verb.View, Subject.PaymentMethods);
    can(Verb.View, Subject.GiftVouchers);
  }

  if (role === "MANAGER") {
    can(Verb.View, Subject.Dashboard);
    can(Verb.View, Subject.StaffCalendar);
    can(Verb.View, Subject.Leads);
    can(Verb.View, Subject.MemberList);
    can(Verb.Download, Subject.MemberList);
    can(Verb.View, Subject.MemberProfile);
    can(Verb.Create, Subject.Email);
    can(Verb.Create, Subject.SMS);
    can(Verb.View, Subject.Settings);
    can(Verb.View, Subject.Sales);
    can(Verb.View, Subject.Reports);
    can(Verb.View, Subject.GlobalSearch);
    can(Verb.Credit, Subject.Invoices);
    can(Verb.Collect, Subject.Invoices);
    can(Verb.Refund, Subject.Invoices);
    can(Verb.WriteOff, Subject.Invoices);
    can(Verb.View, Subject.Marketing);
    can(Verb.View, Subject.Task);
    can(Verb.View, Subject.Appointments);
    can(Verb.View, Subject.AppointmentAvailability);
    can(Verb.View, Subject.FacilityAvailability);
    can(Verb.View, Subject.Access);
    can(Verb.Edit, Subject.CheckoutPrice);
  }

  if (role === "RECEPTION") {
    can(Verb.View, Subject.StaffCalendar);
    can(Verb.View, Subject.Leads);
    can(Verb.View, Subject.MemberList);
    can(Verb.View, Subject.MemberProfile);
    can(Verb.Create, Subject.Email);
    can(Verb.Create, Subject.SMS);
    can(Verb.View, Subject.Sales);
    can(Verb.View, Subject.GlobalSearch);
    can(Verb.Credit, Subject.Invoices);
    can(Verb.Collect, Subject.Invoices);
    can(Verb.WriteOff, Subject.Invoices);
    can(Verb.View, Subject.Marketing);
    can(Verb.View, Subject.Task);
    can(Verb.View, Subject.Appointments);
    can(Verb.View, Subject.AppointmentAvailability, {
      id: { $eq: userId },
    });
    can(Verb.View, Subject.FacilityAvailability);
    can(Verb.View, Subject.Access);
  }

  if (role === "TRAINER") {
    can(Verb.View, Subject.StaffCalendar, {
      "event.userEventHost.id": userId,
    });
    can(Verb.View, Subject.Leads);
    can(Verb.View, Subject.MemberProfile, {
      assignedStaffMembers: { $elemMatch: { id: userId } },
    });
    can(Verb.View, Subject.Sales);
    can(Verb.Credit, Subject.Invoices);
    can(Verb.Collect, Subject.Invoices);
    can(Verb.WriteOff, Subject.Invoices);
    can(Verb.View, Subject.Task);
    can(Verb.View, Subject.Appointments);
    can(Verb.View, Subject.AppointmentAvailability, {
      id: { $eq: userId },
    });
    can(Verb.View, Subject.FacilityAvailability);
    can(Verb.View, Subject.Access);
  }
  if (stripeAccountType === "NOT_CONNECTED") {
    can(Verb.Create, Subject.StripeConnect);
    cannot(Verb.View, Subject.MemberList);
    cannot(Verb.View, Subject.MemberProfile);
    cannot(Verb.View, Subject.Sales);
    cannot(Verb.View, Subject.GlobalSearch);
  }

  if (
    stripeAccountType === "STANDARD_WHILE_ON_BOARDING" ||
    stripeAccountType === "READY_TO_CONNECT"
  ) {
    can(Verb.Create, Subject.StripeConnect);
  }

  if (stripeAccountType === "CONNECTED") {
    can(Verb.View, Subject.StripeConnect);
  }
};

export const create = ({
  role,
  userId,
  stripeAccountType,
}: {
  role: StaffUserRole;
  userId: string;
  stripeAccountType: StripeAccountType;
}) => {
  const builder = new AbilityBuilder(createMongoAbility);

  setupPermissions({ builder, role, userId, stripeAccountType });

  return builder.build();
};

export const update = ({
  ability,
  role,
  userId,
  stripeAccountType,
}: {
  ability: MongoAbility<AbilityTuple, MongoQuery>;
  role: StaffUserRole;
  userId: string;
  stripeAccountType: StripeAccountType;
}) => {
  const builder = new AbilityBuilder(createMongoAbility);

  setupPermissions({ builder, role, userId, stripeAccountType });
  ability.update(builder.rules);
};
